import { auth } from "@/lib/picasso-firebase";
import { getImageForDownload } from "@/api/imageApi";

/**
 * Calculate GCD (Greatest Common Divisor) using Euclidean algorithm
 */
const gcd = (a: number, b: number): number => {
  return b === 0 ? a : gcd(b, a % b);
};

/**
 * Normalize aspect ratio to standard format
 * Converts raw pixel dimensions to standard aspect ratios (16:9, 9:16, 1:1, 4:3, 3:4, etc.)
 * @param aspectRatio - Aspect ratio string in format "width:height"
 * @returns Normalized aspect ratio string
 */
export const normalizeAspectRatio = (aspectRatio?: string): string => {
  if (!aspectRatio) return '1:1';
  
  const parts = aspectRatio.split(':');
  if (parts.length !== 2) return aspectRatio;
  
  const width = Number.parseInt(parts[0], 10);
  const height = Number.parseInt(parts[1], 10);
  
  if (Number.isNaN(width) || Number.isNaN(height) || width <= 0 || height <= 0) {
    return aspectRatio;
  }
  
  // Calculate the actual ratio as a decimal
  const ratio = width / height;
  
  // Define standard aspect ratios with tolerance
  const standardRatios: { ratio: number; label: string }[] = [
    { ratio: 1, label: '1:1' },      // Square (1:1)
    { ratio: 16/9, label: '16:9' },    // Landscape (1.778)
    { ratio: 9/16, label: '9:16' },    // Portrait (0.5625)
    { ratio: 4/3, label: '4:3' },      // Classic landscape (1.333)
    { ratio: 3/4, label: '3:4' },      // Classic portrait (0.75)
    { ratio: 21/9, label: '21:9' },    // Ultra-wide (2.333)
    { ratio: 9/21, label: '9:21' },    // Ultra-tall (0.428)
    { ratio: 3/2, label: '3:2' },      // Photo landscape (1.5)
    { ratio: 2/3, label: '2:3' },      // Photo portrait (0.667)
  ];
  
  // Find the closest standard ratio
  let closestRatio = standardRatios[0];
  let minDifference = Math.abs(ratio - closestRatio.ratio);
  
  for (const standard of standardRatios) {
    const difference = Math.abs(ratio - standard.ratio);
    if (difference < minDifference) {
      minDifference = difference;
      closestRatio = standard;
    }
  }
  
  // If the difference is very small (within 5% tolerance), use the standard ratio
  const tolerance = 0.05;
  if (minDifference / closestRatio.ratio <= tolerance) {
    return closestRatio.label;
  }
  
  // If no close match, calculate simplified ratio using GCD
  const divisor = gcd(width, height);
  const simplifiedWidth = width / divisor;
  const simplifiedHeight = height / divisor;
  
  return `${simplifiedWidth}:${simplifiedHeight}`;
};

export const downloadImageFromUrl = async (signedUrl: string, filename: string): Promise<void> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      alert("Authentication required");
      return;
    }

    const imageBase64 = await getImageForDownload(signedUrl);
    
    if (!imageBase64) {
      throw new Error("No image data received from server");
    }
    
    const byteCharacters = atob(imageBase64);
    const byteArray = new Uint8Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteArray[i] = byteCharacters.codePointAt(i) ?? 0;
    }
    const blob = new Blob([byteArray], { type: 'image/jpeg' });
    
    const url = globalThis.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    globalThis.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Failed to download image:", error);
    alert("Failed to download image. Please try again.");
  }
};
